import { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import ImageGrid from '../components/ImageGrid';
import DownloadButton from '../components/DownloadButton';
import { extractFrames } from '../utils/videoProcessor';
import { probeVideoMeta } from '../utils/probeVideoMeta';
import { usePyodide, loadScript } from '../utils/pyodideLoader';

const PY_SCRIPT_PATH = '/python/frame_extraction/extractor.py';

const ACCEPTED_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];
const ACCEPTED_EXTENSIONS = ['.mp4', '.mov', '.webm'];

// Conservative default used during SSR/prerender, before client detection runs.
const DEFAULT_LIMITS = { maxMB: 500, warnMB: 300 };

// Picks an upload size tier based on device capability. Computed on the client
// only — we never trust SSR/prerender to know the user's device. iOS Safari
// hides navigator.deviceMemory entirely, so for iPhone/iPad we fall back to the
// iOS major version from the UA as a coarse proxy for hardware class
// (iOS 18+ ≈ iPhone 15 Pro and newer with 8 GB RAM). Android exposes
// deviceMemory like other Chromium platforms, so it falls through.
function getDeviceTier() {
  if (typeof navigator === 'undefined') return DEFAULT_LIMITS;
  const ua = navigator.userAgent || '';

  if (/iPhone|iPad|iPod/i.test(ua)) {
    const m = ua.match(/OS (\d+)[_.]/);
    const ios = m ? parseInt(m[1], 10) : null;
    if (ios != null && ios >= 18) return { maxMB: 1024, warnMB: 600 };
    if (ios != null && ios >= 16) return { maxMB: 500, warnMB: 300 };
    return { maxMB: 250, warnMB: 150 }; // older or unparsable
  }

  const mem = navigator.deviceMemory;
  if (mem == null) return DEFAULT_LIMITS; // desktop Safari/Firefox don't expose this.
  if (mem <= 2) return { maxMB: 250, warnMB: 150 };
  if (mem <= 4) return { maxMB: 500, warnMB: 300 };
  if (mem <= 8) return { maxMB: 1024, warnMB: 600 };
  return { maxMB: 2048, warnMB: 1024 };
}

function formatLimit(mb) {
  return mb >= 1024 ? `${(mb / 1024).toFixed(mb % 1024 === 0 ? 0 : 1)} GB` : `${mb} MB`;
}

function isAcceptedVideo(file) {
  if (ACCEPTED_TYPES.includes(file.type)) return true;
  const name = file.name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
}

// A clean container label (MP4 / MOV / WebM). Browsers report inconsistent
// MIME types — sometimes "video/quicktime", sometimes empty — so prefer the
// file extension and fall back to the MIME subtype.
function formatVideoType(file) {
  if (!file) return '—';
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  const byExt = { mp4: 'MP4', m4v: 'MP4', mov: 'MOV', webm: 'WebM' };
  if (byExt[ext]) return byExt[ext];
  const sub = (file.type || '').split('/')[1];
  return sub ? sub.toUpperCase() : ext ? ext.toUpperCase() : '—';
}

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
  return `${(mb / 1024).toFixed(1)} GB`;
}

function aspectRatio(w, h) {
  if (!w || !h) return '—';
  const gcd = (a, b) => (b ? gcd(b, a % b) : a);
  const d = gcd(w, h);
  return `${w / d}:${h / d}`;
}

function fmtTime(s) {
  if (!isFinite(s) || s < 0) return '00:00';
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${r.toFixed(2).padStart(5, '0')}`;
}

// Parse "M:SS(.ss)" / "H:MM:SS" / plain seconds into a number of seconds.
// Returns null if the text isn't a valid time.
function parseTime(str) {
  const parts = String(str).trim().split(':').map((p) => p.trim());
  if (parts.some((p) => p === '' || Number.isNaN(Number(p)))) return null;
  return parts.reduce((acc, p) => acc * 60 + Number(p), 0);
}

// Read a Blob as a base64 string (the body, no `data:` URL prefix).
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      resolve(typeof result === 'string' ? result.split(',', 2)[1] : '');
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function EngineToggle({ engine, onChange, disabled }) {
  const options = [
    { value: 'js', label: 'JavaScript' },
    { value: 'python', label: 'Python' },
  ];
  return (
    <div className="engine-toggle" role="group" aria-label="Compute engine">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          data-active={engine === opt.value}
          onClick={() => onChange(opt.value)}
          disabled={disabled}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function FrameExtractor() {
  const [phase, setPhase] = useState('idle'); // 'idle' | 'extracting' | 'done' | 'error'
  const [file, setFile] = useState(null);
  const [videoURL, setVideoURL] = useState(null);
  const [frames, setFrames] = useState([]);
  const [progressPercent, setProgressPercent] = useState(0);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [limits, setLimits] = useState(DEFAULT_LIMITS);
  const [hot, setHot] = useState(false);

  // Resolve real device-aware upload limits on the client only, after hydration.
  useEffect(() => {
    setLimits(getDeviceTier());
  }, []);

  // ── Source / settings ──────────────────────────────────────────────
  const [duration, setDuration] = useState(0);
  const [now, setNow] = useState(0);
  const [dims, setDims] = useState(null); // { w, h } native video resolution
  const [containerMeta, setContainerMeta] = useState(null); // { codec, fps, bitrate, brand } | null
  // Two flags that gate the "Reading video…" overlay. Both must be true
  // before we consider the file fully inspected: the <video> element has
  // parsed metadata (duration, dimensions), and the container probe has
  // resolved (resolves null fast for non-ISO-BMFF inputs).
  const [metaReady, setMetaReady] = useState(false);
  const [probeDone, setProbeDone] = useState(false);
  const [interval, setInterval] = useState(1);
  const [startT, setStartT] = useState(0);
  const [endT, setEndT] = useState(0);
  const [maxFrames, setMaxFrames] = useState(48);
  const [width, setWidth] = useState(640);
  const [format, setFormat] = useState('jpg');
  const [quality, setQuality] = useState(0.95);

  // ── Engine selection (JS default; Python opt-in via Pyodide) ──────────
  const [engine, setEngine] = useState('js');
  const [scores, setScores] = useState({});           // { [frameNumber]: { sharpness, ... } }
  const [scriptReady, setScriptReady] = useState(false);
  const [scriptError, setScriptError] = useState(null);

  // Only fetch the 10 MB Pyodide runtime when the user actually picks Python.
  const pyodideEnabled = engine === 'python';
  const {
    pyodide,
    loading: pyLoading,
    error: pyError,
  } = usePyodide({ enabled: pyodideEnabled });

  const videoRef = useRef(null);

  // generationRef: every new extract or reset bumps this. Callbacks captured
  // by an in-flight extractFrames check it before mutating state — so if the
  // user hits "Replace video" or navigates away mid-extraction, late frames
  // can't resurrect cleared state. We also revoke their object URLs inline
  // so they don't leak.
  const generationRef = useRef(0);
  const framesRef = useRef([]);
  const videoURLRef = useRef(null);

  // Cleanup on unmount.
  useEffect(
    () => () => {
      generationRef.current += 1;
      framesRef.current.forEach((f) => URL.revokeObjectURL(f.thumbnailUrl));
      framesRef.current = [];
      if (videoURLRef.current) URL.revokeObjectURL(videoURLRef.current);
    },
    []
  );

  // When Pyodide is ready, load extractor.py once. Subsequent runs reuse it.
  useEffect(() => {
    if (!pyodide || scriptReady) return undefined;
    let cancelled = false;
    loadScript(PY_SCRIPT_PATH)
      .then(() => {
        if (cancelled) return;
        setScriptReady(true);
        setScriptError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setScriptError(err);
      });
    return () => {
      cancelled = true;
    };
  }, [pyodide, scriptReady]);

  // If Pyodide or the script fails to load, transparently fall back to JS.
  // The status pill in the settings panel surfaces what happened.
  useEffect(() => {
    if ((pyError || scriptError) && engine === 'python') {
      setEngine('js');
    }
  }, [pyError, scriptError, engine]);

  // Score a frame in Python. Best-effort and async — failures are logged
  // and the JS extraction keeps going.
  const scoreFrameInPython = useCallback(
    async (frame, gen) => {
      if (!pyodide || !scriptReady) return;
      let result = null;
      try {
        const b64 = await blobToBase64(frame.blob);
        if (gen !== generationRef.current) return;
        // Pass through pyodide.globals so we don't string-interpolate a
        // huge base64 blob into the source we hand to runPythonAsync.
        pyodide.globals.set('vl_image_b64', b64);
        result = await pyodide.runPythonAsync('analyze_frame(vl_image_b64)');
        pyodide.globals.delete('vl_image_b64');
        if (gen !== generationRef.current) return;
        const obj =
          result && typeof result.toJs === 'function'
            ? result.toJs({ dict_converter: Object.fromEntries })
            : result;
        setScores((prev) => ({ ...prev, [frame.frameNumber]: obj }));
      } catch (e) {
        // Per-frame failures shouldn't break the run.
        // eslint-disable-next-line no-console
        console.warn(
          `[Python] sharpness analysis failed for frame ${frame.frameNumber}:`,
          e
        );
      } finally {
        if (result && typeof result.destroy === 'function') result.destroy();
      }
    },
    [pyodide, scriptReady]
  );

  // ── File handling ───────────────────────────────────────────────────
  const loadFile = (f) => {
    if (!f) return;
    setError(null);
    if (!isAcceptedVideo(f)) {
      setError("That file format isn't supported. Try an MP4, MOV, or WebM video.");
      return;
    }
    const maxBytes = limits.maxMB * 1024 * 1024;
    const warnBytes = limits.warnMB * 1024 * 1024;
    if (f.size > maxBytes) {
      setError(
        `That video is ${formatBytes(f.size)}. On this device, please keep it under ${formatLimit(limits.maxMB)}.`
      );
      return;
    }
    setWarning(
      f.size > warnBytes
        ? `Large video (${formatBytes(f.size)}). Extraction may take a few minutes and use significant memory — closing other tabs first will help.`
        : null
    );
    // Tear down any prior video + frames.
    generationRef.current += 1;
    framesRef.current.forEach((fr) => URL.revokeObjectURL(fr.thumbnailUrl));
    framesRef.current = [];
    if (videoURLRef.current) URL.revokeObjectURL(videoURLRef.current);

    const url = URL.createObjectURL(f);
    videoURLRef.current = url;
    setFile(f);
    setVideoURL(url);
    setFrames([]);
    setScores({});
    setContainerMeta(null);
    setMetaReady(false);
    setProbeDone(false);
    setProgressPercent(0);
    setPhase('idle');

    // Probe ISO BMFF (MP4/MOV) headers for codec/fps/bitrate. Returns null
    // for WebM and anything else mp4box can't parse — meta rows fall back
    // to "—" in that case.
    const gen = generationRef.current;
    probeVideoMeta(f).then((meta) => {
      if (gen !== generationRef.current) return;
      setContainerMeta(meta);
      setProbeDone(true);
    });
  };

  const onPick = (e) => loadFile(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault();
    setHot(false);
    loadFile(e.dataTransfer.files?.[0]);
  };

  const onMeta = () => {
    const v = videoRef.current;
    if (!v) return;
    setDuration(v.duration);
    setDims({ w: v.videoWidth, h: v.videoHeight });
    setMetaReady(true);
    setWidth(v.videoWidth); // default to native resolution (no downscale)
    setStartT(0);
    setEndT(v.duration);
  };

  // Keep "Max frames" at whole-video coverage for the current interval, so the
  // cap never silently truncates the video. Recomputed on load and whenever
  // the interval changes; the user can still type a lower cap afterwards.
  useEffect(() => {
    if (!duration) return;
    setMaxFrames(Math.floor(duration / Math.max(0.05, interval)) + 1);
  }, [duration, interval]);
  const onTimeUpdate = () => setNow(videoRef.current?.currentTime || 0);

  const estimateCount = () => {
    if (!duration) return 0;
    const span = Math.max(0, endT - startT);
    const n = Math.floor(span / Math.max(0.05, interval)) + 1;
    return Math.min(n, maxFrames);
  };

  const handleExtract = async () => {
    if (!file) return;
    generationRef.current += 1;
    const myGen = generationRef.current;
    framesRef.current.forEach((fr) => URL.revokeObjectURL(fr.thumbnailUrl));
    framesRef.current = [];

    setFrames([]);
    setProgressPercent(0);
    setError(null);
    setScores({});
    setPhase('extracting');

    // Capture once — if the user toggles engines mid-extraction, this run
    // sticks with whatever was selected when the user clicked Extract.
    const usePython = engine === 'python' && !!pyodide && scriptReady;

    try {
      await extractFrames(file, {
        interval,
        quality,
        startTime: startT,
        endTime: endT,
        maxFrames,
        width,
        format,
        onProgress: (p) => {
          if (myGen !== generationRef.current) return;
          setProgressPercent(p);
        },
        onFrame: (frame) => {
          if (myGen !== generationRef.current) {
            URL.revokeObjectURL(frame.thumbnailUrl);
            return;
          }
          framesRef.current = [...framesRef.current, frame];
          setFrames(framesRef.current);
          if (usePython) {
            // Fire-and-forget; the next seek can start before this finishes.
            scoreFrameInPython(frame, myGen);
          }
        },
      });
      if (myGen === generationRef.current) setPhase('done');
    } catch (err) {
      if (myGen === generationRef.current) {
        setError(err.message || 'Something went wrong while extracting frames.');
        setPhase('error');
      }
    }
  };

  const replaceVideo = () => {
    generationRef.current += 1;
    framesRef.current.forEach((f) => URL.revokeObjectURL(f.thumbnailUrl));
    framesRef.current = [];
    if (videoURLRef.current) URL.revokeObjectURL(videoURLRef.current);
    videoURLRef.current = null;
    setFile(null);
    setVideoURL(null);
    setFrames([]);
    setScores({});
    setProgressPercent(0);
    setError(null);
    setDuration(0);
    setNow(0);
    setDims(null);
    setWarning(null);
    setPhase('idle');
  };

  const zipFilename = file
    ? `${file.name.replace(/\.[^/.]+$/, '')}-frames.zip`
    : 'frames.zip';

  const pythonActive = engine === 'python' && !!pyodide && scriptReady;
  const scoredCount = Object.keys(scores).length;

  // Build the inline status line for the engine row.
  let pythonStatus = null;
  if (engine === 'python') {
    if (pyError) {
      pythonStatus = { kind: 'error', text: 'Couldn’t start Python. Falling back to JavaScript.' };
    } else if (scriptError) {
      pythonStatus = { kind: 'error', text: 'Couldn’t load extractor.py. Falling back to JavaScript.' };
    } else if (pyLoading || !pyodide) {
      pythonStatus = { kind: 'info', text: 'Loading Python runtime…' };
    } else if (!scriptReady) {
      pythonStatus = { kind: 'info', text: 'Loading extractor.py…' };
    } else {
      pythonStatus = { kind: 'ok', text: 'Ready' };
    }
  }

  const head = (
    <Helmet>
      <title>Frame Extractor · VantageLabs</title>
      <meta
        name="description"
        content="Pull high-resolution stills from any video at a chosen interval. Everything happens in your browser. Supports MP4, MOV, and WebM. No upload, no server."
      />
      <link rel="canonical" href="https://vantage.lodonu.dev/frame-extractor" />
    </Helmet>
  );

  // ── Empty state: drop zone ──────────────────────────────────────────
  if (!videoURL) {
    return (
      <>
        {head}
        <div className="ws-pane-head">
          <span className="ws-pane-title">Frame Extractor</span>
          <span className="ws-pane-spacer" />
        </div>
        <div className="ws-pane-body ws-pane-body-flex">
          <label
            className={`ws-drop${hot ? ' hot' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              setHot(true);
            }}
            onDragLeave={() => setHot(false)}
            onDrop={onDrop}
          >
            <input
              type="file"
              accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm"
              onChange={onPick}
              className="ws-drop-input"
            />
            <span className="ws-drop-glyph" aria-hidden="true">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="6" width="14" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
                <path d="M17 10l4-2v8l-4-2z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
              </svg>
            </span>
            <span className="ws-drop-text">
              <span className="ws-drop-title">
                Drop a video to extract <span className="ws-accent-italic">frames</span>.
              </span>
              <span className="ws-drop-hint">
                MP4, MOV, or WebM up to {formatLimit(limits.maxMB)}. Nothing
                leaves your device. Everything happens right here in your browser.
              </span>
              <span className="ws-drop-actions">
                <span className="ws-btn ws-btn-primary">Choose file</span>
                <span className="mono">or drag &amp; drop</span>
              </span>
              {error && <span className="ws-drop-err mono">{error}</span>}
            </span>
          </label>
        </div>
      </>
    );
  }

  // ── Loaded state ────────────────────────────────────────────────────
  const count = estimateCount();
  // What the current settings will actually produce — we never upscale past
  // the video's native width. Shown live so width/format/quality have visible
  // consequences before you extract.
  const outW = dims ? Math.min(Math.round(width), dims.w) : Math.round(width);
  const outH = dims && dims.w ? Math.round(outW * (dims.h / dims.w)) : 0;

  return (
    <>
      {head}
      <div className="ws-pane-head">
        <span className="ws-pane-title">Frame Extractor</span>
        <span className="ws-crumbs">/ {file?.name || 'video'}</span>
        <span className="ws-pane-spacer" />
        <button className="ws-btn ws-btn-primary" type="button" onClick={replaceVideo}>
          Replace video
        </button>
      </div>

      <div className="ws-pane-body">
        {warning && (
          <p className="drop-warning" role="status" style={{ marginBottom: 16 }}>
            {warning}
          </p>
        )}
        <div className="ws-ext-layout">
          {/* ── LEFT: video + output ── */}
          <div className="ws-col">
            <div className="ws-panel ws-panel-source">
              <div className="ws-panel-head">
                <span className="ws-panel-t">Source</span>
                <span className="mono ws-panel-file">{file?.name}</span>
                <span className="ws-pane-spacer" />
                <span className="mono ws-nowrap">
                  {fmtTime(now)} / {fmtTime(duration)}
                </span>
              </div>
              <div className="ws-panel-body ws-source-body">
                {file && !(metaReady && probeDone) && (
                  <div className="ws-source-loading" role="status" aria-live="polite">
                    <span className="ws-spinner" aria-hidden="true" />
                    <span className="mono">Reading video…</span>
                  </div>
                )}
                <div className="ws-source-row">
                  <div className="ws-source-left">
                    <div
                      className="ws-video-wrap"
                      style={dims ? { aspectRatio: `${dims.w} / ${dims.h}` } : undefined}
                    >
                      <video
                        ref={videoRef}
                        src={videoURL}
                        onLoadedMetadata={onMeta}
                        onTimeUpdate={onTimeUpdate}
                        controls
                        preload="metadata"
                      />
                    </div>

                    <div className="ws-rangebar-wrap">
                      <RangeBar
                        duration={duration}
                        start={startT}
                        end={endT}
                        interval={interval}
                        maxFrames={maxFrames}
                        onStart={setStartT}
                        onEnd={setEndT}
                      />
                    </div>
                  </div>

                  <dl className="ws-meta-list">
                    <MetaRow label="Type" tip="The container format of the video (e.g. MP4, MOV, WebM).">
                      {formatVideoType(file)}
                    </MetaRow>
                    <MetaRow label="Aspect" tip="Shape of the frame — the width-to-height ratio.">
                      {dims ? aspectRatio(dims.w, dims.h) : '—'}
                    </MetaRow>
                    <MetaRow label="Size" tip="How large the video file is on disk.">
                      {file ? formatBytes(file.size) : '—'}
                    </MetaRow>
                    {containerMeta?.fps && (
                      <MetaRow label="Frame rate" tip="How many frames the video shows each second.">
                        {containerMeta.fps.toFixed(2)} fps
                      </MetaRow>
                    )}
                    {containerMeta?.bitrate && (
                      <MetaRow
                        label="Bitrate"
                        tip="How much data the video uses per second — higher usually means better quality."
                      >
                        {(containerMeta.bitrate / 1_000_000).toFixed(2)} Mbps
                      </MetaRow>
                    )}
                    <MetaRow label="Resolution" tip="Width × height of each frame, in pixels.">
                      {dims ? `${dims.w} × ${dims.h}` : '—'}
                    </MetaRow>
                    <MetaRow label="Duration" tip="How long the video plays from start to end.">
                      {fmtTime(duration)}
                    </MetaRow>
                    {containerMeta?.codec && (
                      <MetaRow label="Codec" tip="The algorithm used to compress the video." valueTitle={containerMeta.codec}>
                        {containerMeta.codec}
                      </MetaRow>
                    )}
                    <MetaRow label="File" tip="The name of the file you uploaded." valueTitle={file?.name}>
                      {file?.name || '—'}
                    </MetaRow>
                  </dl>
                </div>
              </div>
            </div>

            <div className="ws-panel ws-panel-grow ws-panel-output">
              <div className="ws-panel-head">
                <span className="ws-panel-t">Output</span>
                <span className="mono">
                  {frames.length} / {count} frames
                </span>
                {pythonActive && scoredCount > 0 && (
                  <span
                    className="python-badge"
                    title="Frame metrics computed in-browser by Pyodide running python/frame_extraction/extractor.analyze_frame"
                  >
                    <span className="python-badge-dot" aria-hidden="true" />
                    {scoredCount} analysed
                  </span>
                )}
                <span className="ws-pane-spacer" />
                {!running(phase) && frames.length > 0 && (
                  <DownloadButton mode="zip" frames={frames} filename={zipFilename} />
                )}
              </div>
              <div className="ws-panel-body ws-output-body">
                {frames.length === 0 && phase !== 'extracting' && <EmptyOutput />}
                {phase === 'extracting' && (
                  <div className="ws-progress-wrap">
                    <ProgressBar percent={progressPercent} done={false} />
                  </div>
                )}
                {frames.length > 0 && <ImageGrid frames={frames} scores={scores} />}
              </div>
            </div>
          </div>

          {/* ── RIGHT: controls ── */}
          <div className="ws-col">
            <div className="ws-panel ws-panel-settings">
              <div className="ws-panel-head">
                <span className="ws-panel-t">Settings</span>
                <span className="ws-pane-spacer" />
                <span className="mono">~{count} frames</span>
              </div>
              <div className="ws-panel-body ws-settings">
                <label className="ws-lbl">
                  <span className="ws-lbl-nm">Interval (seconds)</span>
                  <input
                    className="ws-input"
                    type="number"
                    min="0.05"
                    step="0.05"
                    value={interval}
                    onChange={(e) => setInterval(Math.max(0.05, Number(e.target.value)))}
                  />
                </label>

                <div className="ws-control-row">
                  <label className="ws-lbl">
                    <span className="ws-lbl-nm">Start (mm:ss)</span>
                    <TimeField
                      seconds={startT}
                      max={endT}
                      onCommit={setStartT}
                    />
                  </label>
                  <label className="ws-lbl">
                    <span className="ws-lbl-nm">End (mm:ss)</span>
                    <TimeField
                      seconds={endT}
                      max={duration}
                      onCommit={(s) => setEndT(Math.max(startT, s))}
                    />
                  </label>
                </div>

                <div className="ws-control-row">
                  <label className="ws-lbl">
                    <span className="ws-lbl-nm">Max frames</span>
                    <input
                      className="ws-input"
                      type="number"
                      min="1"
                      max="2000"
                      value={maxFrames}
                      onChange={(e) => setMaxFrames(Math.max(1, Number(e.target.value) || 1))}
                    />
                  </label>
                  <label className="ws-lbl">
                    <span className="ws-lbl-nm">Width (px)</span>
                    <input
                      className="ws-input"
                      type="number"
                      min="64"
                      step="16"
                      value={width}
                      onChange={(e) => setWidth(Math.max(64, Number(e.target.value) || 64))}
                    />
                  </label>
                </div>

                <div className="ws-control-row">
                  <label className="ws-lbl">
                    <span className="ws-lbl-nm">Format</span>
                    <select
                      className="ws-select"
                      value={format}
                      onChange={(e) => setFormat(e.target.value)}
                    >
                      <option value="jpg">JPG</option>
                      <option value="png">PNG</option>
                    </select>
                  </label>
                  <label className="ws-lbl">
                    <span className="ws-lbl-nm">Quality</span>
                    <input
                      className="ws-input"
                      type="number"
                      min="0.1"
                      max="1"
                      step="0.05"
                      value={quality}
                      disabled={format === 'png'}
                      onChange={(e) =>
                        setQuality(Math.max(0.1, Math.min(1, Number(e.target.value))))
                      }
                    />
                  </label>
                </div>

                <div className="ws-output-spec">
                  <span className="ws-lbl-nm">Output</span>
                  <span className="ws-output-spec-val mono">
                    {outW} × {outH} · {format.toUpperCase()}
                    {format === 'jpg' ? ` · q${quality}` : ''}
                    {dims && width > dims.w ? ' · capped to native' : ''}
                  </span>
                </div>

                <div className="ws-engine-row">
                  <span className="ws-lbl-nm">Engine</span>
                  <EngineToggle
                    engine={engine}
                    onChange={setEngine}
                    disabled={phase === 'extracting'}
                  />
                  {pythonStatus && (
                    <span className="engine-status" data-kind={pythonStatus.kind}>
                      {pythonStatus.text}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <button
              className="ws-btn ws-btn-primary ws-extract-btn"
              type="button"
              disabled={
                phase === 'extracting' ||
                !duration ||
                (!!file && !(metaReady && probeDone))
              }
              onClick={handleExtract}
            >
              {phase === 'extracting'
                ? 'Extracting…'
                : file && !(metaReady && probeDone)
                  ? 'Reading video…'
                  : `Extract ${count} frame${count === 1 ? '' : 's'} →`}
            </button>

            {phase === 'error' && error && (
              <div className="ws-drop-err mono" role="alert">
                {error}
              </div>
            )}

            <div className="ws-panel ws-panel-abouttool">
              <div className="ws-panel-head">
                <span className="ws-panel-t ws-panel-t-sm">About this tool</span>
              </div>
              <div className="ws-panel-body ws-about-tool">
                <p>
                  Frames are decoded by your browser's video element and rasterised
                  to canvas at the timestamps you set. The output is a sequence of
                  still images you can download individually or as a ZIP.
                </p>
                <p className="ws-muted">
                  Tip: use a smaller width (320–480) when scrubbing long clips for
                  speed — re-export at full resolution once you've found the
                  interesting interval. Switch the engine to Python to score each
                  frame's sharpness in-browser.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// One labelled metadata cell. The label is a button that toggles an inline
// tooltip on tap (mobile) or hover/focus (desktop). Tapping outside or
// pressing Escape closes the open tooltip.
function MetaRow({ label, tip, valueTitle, children }) {
  const [open, setOpen] = useState(false);
  const rowRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => {
      if (!rowRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="ws-meta-row" ref={rowRef}>
      <dt>
        <button
          type="button"
          className="ws-meta-tip-btn"
          aria-expanded={open}
          aria-label={`${label}: ${tip}`}
          onClick={() => setOpen((o) => !o)}
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
        >
          {label}
        </button>
      </dt>
      <dd title={valueTitle}>{children}</dd>
      {open && (
        <span className="ws-meta-tip" role="tooltip">
          {tip}
        </span>
      )}
    </div>
  );
}

function running(phase) {
  return phase === 'extracting';
}

// A text field that shows/edits a time as mm:ss(.ss). It mirrors the seconds
// prop while unfocused (so range-bar drags update it), and commits the parsed
// value on blur / Enter so typing isn't reformatted mid-keystroke.
function TimeField({ seconds, max, onCommit }) {
  const [text, setText] = useState(fmtTime(seconds));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setText(fmtTime(seconds));
  }, [seconds, focused]);

  const commit = () => {
    const parsed = parseTime(text);
    if (parsed == null) {
      setText(fmtTime(seconds)); // invalid — revert
      return;
    }
    onCommit(Math.max(0, Math.min(max, parsed)));
  };

  return (
    <input
      className="ws-input"
      type="text"
      inputMode="numeric"
      value={text}
      onFocus={() => setFocused(true)}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        setFocused(false);
        commit();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          commit();
          e.currentTarget.blur();
        }
      }}
    />
  );
}

// Interactive extract-window bar. Drag the left/right handles to move each
// edge, or drag the highlighted span to slide the whole window while keeping
// its length. Edits flow back up through onStart / onEnd.
function RangeBar({ duration, start, end, interval, maxFrames, onStart, onEnd }) {
  const trackRef = useRef(null);
  const MIN_SPAN = 0.05; // keep at least one interval's worth of window

  if (!duration) return null;
  const sp = (start / duration) * 100;
  const ep = (end / duration) * 100;
  const ticks = [];
  const n = Math.min(maxFrames, Math.floor((end - start) / Math.max(0.05, interval)) + 1);
  for (let i = 0; i < n; i++) {
    const t = start + i * interval;
    if (t > end) break;
    ticks.push((t / duration) * 100);
  }

  const timeFromX = (clientX) => {
    const rect = trackRef.current.getBoundingClientRect();
    const ratio = (clientX - rect.left) / rect.width;
    return Math.max(0, Math.min(duration, ratio * duration));
  };

  // mode: 'start' | 'end' | 'span'
  const beginDrag = (mode) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const t0 = timeFromX(e.clientX);
    const win = end - start;

    const onMove = (ev) => {
      const t = timeFromX(ev.clientX);
      if (mode === 'start') {
        onStart(Math.max(0, Math.min(t, end - MIN_SPAN)));
      } else if (mode === 'end') {
        onEnd(Math.min(duration, Math.max(t, start + MIN_SPAN)));
      } else {
        let ns = start + (t - t0);
        ns = Math.max(0, Math.min(ns, duration - win));
        onStart(ns);
        onEnd(ns + win);
      }
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  return (
    <div className="ws-rangebar">
      <div className="mono ws-rangebar-label">
        <span>Extract window · drag to move</span>
        <span>
          {fmtTime(start)} → {fmtTime(end)} · {n} ticks
        </span>
      </div>
      <div className="ws-rangebar-track" ref={trackRef}>
        <div
          className="ws-rangebar-span"
          style={{ left: `${sp}%`, width: `${Math.max(0, ep - sp)}%` }}
          onPointerDown={beginDrag('span')}
        />
        {ticks.map((p, i) => (
          <div key={i} className="ws-rangebar-tick" style={{ left: `${p}%` }} />
        ))}
        <div
          className="ws-rangebar-handle"
          style={{ left: `clamp(0px, calc(${sp}% - 6px), calc(100% - 12px))` }}
          onPointerDown={beginDrag('start')}
          role="slider"
          aria-label="Window start"
          aria-valuemin={0}
          aria-valuemax={duration}
          aria-valuenow={start}
        />
        <div
          className="ws-rangebar-handle"
          style={{ left: `clamp(0px, calc(${ep}% - 6px), calc(100% - 12px))` }}
          onPointerDown={beginDrag('end')}
          role="slider"
          aria-label="Window end"
          aria-valuemin={0}
          aria-valuemax={duration}
          aria-valuenow={end}
        />
        <span className="ws-rangebar-end ws-rangebar-end-l">0:00</span>
        <span className="ws-rangebar-end ws-rangebar-end-r">{fmtTime(duration)}</span>
      </div>
    </div>
  );
}

function ProgressBar({ percent }) {
  return (
    <div className="ws-progress">
      <div className="mono ws-progress-label">
        <span>Extracting…</span>
        <span>{Math.round(percent)}%</span>
      </div>
      <div className="ws-progress-track">
        <div className="ws-progress-fill" style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

function EmptyOutput() {
  return (
    <div className="ws-empty-output">
      <div>
        <div className="ws-empty-title">No frames yet</div>
        <div className="mono">Set an interval &amp; range, then hit Extract</div>
      </div>
    </div>
  );
}

export default FrameExtractor;
